import { UniversalTrackerValuePlain } from '../types/universalTrackerValue';
import { UniversalTrackerPlain } from '../types/universalTracker';
import { g17ecoApi, transformResponse } from './g17ecoApi';
import { AccessType, CustomMetricsUsage } from '@g17eco/types/metricGroup';
import type { CustomMetricPayload } from '@features/custom-metrics/types';

export interface CustomMetric extends UniversalTrackerPlain {
  universalTrackerValues: Pick<UniversalTrackerValuePlain, 'status'>[];
  accessType: AccessType.Inherited | AccessType.Custom;
}

export interface CustomMetricsResponse {
  created: CustomMetric[];
  used: CustomMetric[];
}

interface CreateCustomMetricParams {
  initiativeId: string;
  formData: CustomMetricPayload;
}

interface UpdateCustomMetricParams {
  initiativeId: string;
  utrId: string;
  formData: CustomMetricPayload;
}

interface DeleteCustomMetricParams {
  initiativeId: string;
  utrId: string;
}

export const customMetricApi = g17ecoApi
  .enhanceEndpoints({
    addTagTypes: ['initiative-custom-metrics', 'initiative-custom-metrics-usage'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getInitiativeCustomMetrics: builder.query<CustomMetricsResponse, string>({
        transformResponse,
        query: (initiativeId) => {
          return {
            url: `/initiatives/${initiativeId}/custom-metrics`,
            method: 'get',
          };
        },
        providesTags: ['initiative-custom-metrics'],
      }),
      checkIsCustomMetricAnswered: builder.query<{ isAnswered: boolean }, { initiativeId: string; utrId: string }>({
        transformResponse,
        query: ({ initiativeId, utrId }) => {
          return {
            url: `/initiatives/${initiativeId}/metric/${utrId}/check-is-answered`,
            method: 'get',
          };
        },
        providesTags: ['initiative-custom-metrics'],
      }),
      getInitiativeCustomMetricsUsage: builder.query<CustomMetricsUsage, string>({
        transformResponse,
        query: (initiativeId) => {
          return {
            url: `/initiatives/${initiativeId}/custom-metrics/usage`,
            method: 'get',
          };
        },
        providesTags: ['initiative-custom-metrics-usage'],
      }),
      createCustomMetric: builder.mutation<CustomMetric, CreateCustomMetricParams>({
        query: ({ initiativeId, formData }) => ({
          url: `/initiatives/${initiativeId}/metric`,
          method: 'post',
          data: formData,
        }),
        transformResponse,
        invalidatesTags: ['initiative-custom-metrics'],
      }),
      updateCustomMetric: builder.mutation<CustomMetric, UpdateCustomMetricParams>({
        query: ({ initiativeId, utrId, formData }) => ({
          url: `/initiatives/${initiativeId}/metric/${utrId}`,
          method: 'patch',
          data: formData,
        }),
        transformResponse,
        invalidatesTags: ['initiative-custom-metrics'],
      }),
      deleteCustomMetric: builder.mutation<void, DeleteCustomMetricParams>({
        query: ({ initiativeId, utrId }) => ({
          url: `/initiatives/${initiativeId}/metric/${utrId}`,
          method: 'delete',
        }),
        invalidatesTags: ['initiative-custom-metrics'],
      }),
    }),
  });

export const {
  useGetInitiativeCustomMetricsQuery,
  useCheckIsCustomMetricAnsweredQuery,
  useGetInitiativeCustomMetricsUsageQuery,
  useCreateCustomMetricMutation,
  useUpdateCustomMetricMutation,
  useDeleteCustomMetricMutation,
} = customMetricApi;
